import {
  createSignal,
  createContext,
  useContext,
  createEffect,
  createDeferred,
  createMemo,
  on,
  createResource,
} from 'solid-js';
import { createStore, unwrap } from 'solid-js/store';
import { Connector, Dataset } from '@oceanum/datamesh';
import { Transform, useTimer, DataWrapper, spec, specTrack } from 'framework';
import { create } from 'domain';

const [_dataLoading, _setDataLoading] = createStore([]);

const useDataLoading = () => {
  const current = createMemo(() => [..._dataLoading]);
  const loading = () => _dataLoading.length > 0;
  const contains = (ids) => {
    if (!ids) return false;
    return _dataLoading.some((id) => ids.includes(id));
  };
  const includes = (id) => {
    if (!id) return false;
    return _dataLoading.includes(id);
  };

  return {
    current,
    loading,
    contains,
    includes,
    start(id) {
      _setDataLoading((p) => (p.includes(id) ? p : [...p, id]));
    },
    stop(id) {
      _setDataLoading((p) => (p.includes(id) ? p.filter((i) => i !== id) : p));
    },
  };
};

const dataLoading = useDataLoading();

const needsRefresh = (dataspec, timer) => {
  if (!dataspec || !dataspec.query) return false;
  const refresh = dataspec.query.refresh || 60;
  return (
    dataspec.query.timefilter &&
    dataspec.query.timefilter.times[1] === null &&
    timer % refresh == 0
  );
};

const DataContext = createContext({});
const useData = () => useContext(DataContext);

const DataProvider = (props) => {
  const [datamesh, setDatamesh] = createSignal([]);
  const [inline, setInline] = createSignal([]);
  const [zarr, setZarr] = createSignal([]);
  const [data, setData] = createSignal({});
  const [dataSpec, setDataSpec] = createSignal({});
  const [refresh, setRefresh] = createSignal({});
  const [transforms, setTransforms] = createSignal({});
  const [datameshGateway, setDatameshGateway] = createSignal(null);

  const { timer } = useTimer();

  const getZarr = async (url, headers, coordkeys, ttl) => {
    // Strip trailing slash to prevent double-slash in zarr paths
    const cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;
    return await Dataset.zarr(cleanUrl, headers, { coordkeys, ttl });
  };

  const getData = async (query, maxage, resptype = null) => {
    return await datameshGateway().query(unwrap(query), { timeout: 120000 });
  };

  const stageData = async (query) => {
    return await datameshGateway().stageRequest(unwrap(query));
  };

  const dataRegistry = createMemo(() => {
    console.log('Data registry', data(), transforms());
    return {
      ...data(),
      ...transforms(),
    };
  });

  createEffect(() => {
    console.log('updated ', timer());
  });

  createEffect(
    on([props.authToken], () => {
      if (!props.authToken()) {
        console.warn('Must set Oceanum auth to use Datamesh');
        return;
      }
      setDatameshGateway(
        new Connector(null, {
          jwtAuth: props.authToken(),
          service: props.datameshService,
          gateway: props.gatewayService,
        }),
      );
    }),
  );

  createEffect(
    on([() => specTrack(spec.data), props.authToken], () => {
      if (!spec.data?.length) {
        setDatamesh([]);
        setInline([]);
        setZarr([]);
        return;
      }
      const datameshDatasources = spec.data.filter(
        (d) => d.dataType === 'oceanql',
      );
      const inlineData = spec.data.filter(
        (d) => d.dataType === 'dataset' || d.dataType === 'geojson',
      );
      const zarrData = spec.data.filter((d) => d.dataType === 'zarr');
      const transformData = spec.data.filter((d) => d.dataType === 'transform');

      setDatamesh(unwrap(datameshDatasources));
      setInline(unwrap(inlineData));
      setZarr(unwrap(zarrData));

      // Process transforms
      if (transformData.length) {
        //Iterate over any existing transforms and terminate them to tidy up the workers
        Object.keys(transforms()).forEach((id) => {
          transforms()[id].terminate();
        });
        setTransforms({});
        transformData.forEach((transformSpec) => {
          const inputs = transformSpec.dataSpec.inputs;
          const outputType = transformSpec.dataSpec.outputType;
          const modules = unwrap(transformSpec.dataSpec.modules);
          const code = unwrap(transformSpec.dataSpec.code);
          if (modules) {
            for (const module of Object.keys(modules)) {
              if (modules[module].startsWith('/test')) {
                modules[module] = modules[module].replace(
                  '/test',
                  window.location.origin + '/test',
                );
              }
            }
          }

          // We need to wait for the inputs data to be available
          createEffect(
            on([data], () => {
              for (const input of inputs) {
                const inputdata = data()[input];
                if (!inputdata) {
                  console.warn('Input', input, 'not available');
                  return;
                }
              }

              Transform.init(
                {
                  id: transformSpec.id,
                  code,
                  inputs,
                  modules,
                  outputType,
                },
                inputs.map((input) => data()[input]),
              ).then((transform) => {
                setTransforms((prev) => ({
                  ...prev,
                  [transformSpec.id]: transform,
                }));
              });
            }),
          );
        });
      }
    }),
  );

  //Filter and remove any data that is not in the datamesh, inline, or zarr list
  createEffect(
    on([datamesh, inline, zarr], () => {
      const dataIds = datamesh()
        .map((d) => d.id)
        .concat(inline().map((d) => d.id))
        .concat(zarr().map((d) => d.id));
      const filtered = Object.keys(data())
        .filter((key) => dataIds.includes(key))
        .reduce((obj, key) => {
          obj[key] = data()[key];
          return obj;
        }, {});
      setData(filtered);
      //Remove any keys from refresh that are no longer in data
      setRefresh((prev) => {
        const keys = Object.keys(prev);
        return keys.reduce((obj, key) => {
          if (dataIds.includes(key)) {
            obj[key] = prev[key];
          }
          return obj;
        }, {});
      });
    }),
  );

  //Create dataSpec for each datamesh data source
  createEffect(
    on([datamesh, datameshGateway], () => {
      if (!datamesh() || !datameshGateway()) return;
      const currentSpec = unwrap(dataSpec());
      datamesh().map((d) => {
        const queryStr = JSON.stringify(d.dataSpec);
        const prev = currentSpec[d.id];
        if (prev && JSON.stringify(prev.query) === queryStr) return;
        stageData(d.dataSpec).then((stage) => {
          if (!stage) return;
          const newSpec = { query: { ...d.dataSpec }, stage };
          setRefresh((prevR) => ({
            ...prevR,
            [d.id]: prev?.stage?.qhash != stage.qhash,
          }));
          setDataSpec((prevS) => ({
            ...prevS,
            [d.id]: {
              ...newSpec,
            },
          }));
        });
      });
    }),
  );

  //Create data entries for each data source
  createEffect(
    on([dataSpec, timer], () => {
      if (!dataSpec()) return;
      Object.keys(dataSpec()).map((did) => {
        const ds = dataSpec()[did];
        if (
          (refresh()[did] || needsRefresh(ds, timer())) &&
          !dataLoading.includes(did)
        ) {
          console.log('Refreshing:', did, ds.query);
          dataLoading.start(did);
          getData({ ...ds.query, id: did }, 0, ds.stage.container)
            .then((newdata) => {
              if (!!newdata) {
                newdata.coordkeys = ds.stage.coordkeys;
                const wrapped = new DataWrapper(did, newdata, {
                  parameters: ds.query.parameters,
                });
                setData((prev) => ({
                  ...prev,
                  [did]: wrapped,
                }));
                setRefresh((prev) => ({
                  ...prev,
                  [did]: false,
                }));
              }
              dataLoading.stop(did);
            })
            .catch((e) => {
              console.error(e);
              dataLoading.stop(did);
            });
        }
      });
    }),
  );
  createEffect(
    on([inline], () => {
      if (inline()) {
        inline().map((d) => {
          DataWrapper.inline(d.id, d.dataSpec).then((data) => {
            setData((prev) => ({
              ...prev,
              [d.id]: data,
            }));
          });
        });
      }
    }),
  );
  createEffect(
    on([zarr], () => {
      if (zarr()) {
        zarr().map((d) => {
          const id = d.id; // Capture id to avoid closure issues
          let headers = d.dataSpec.headers || {};
          if (d.dataSpec.forwardAuth && props.authToken()) {
            headers = {
              ...headers,
              Authorization: `Bearer ${props.authToken()}`,
            };
          }
          getZarr(
            d.dataSpec.group,
            headers,
            d.dataSpec.coordkeys,
            d.dataSpec.ttl,
          ).then((dataset) => {
            const available = new Set([
              ...Object.keys(dataset.dimensions || {}),
              ...Object.keys(dataset.variables || {}),
            ]);
            for (const [key, name] of Object.entries(
              d.dataSpec.coordkeys || {},
            )) {
              if (name && !available.has(name as string)) {
                console.warn(
                  `Datasource "${id}" coordkey ${key}="${name}" not found in dataset (available: ${[...available].join(', ')})`,
                );
              }
            }
            const wrapped = new DataWrapper(id, dataset);
            setData((prev) => ({
              ...prev,
              [id]: wrapped,
            }));
          });
        });
      }
    }),
  );

  return (
    <DataContext.Provider
      value={{
        dataSpec,
        dataRegistry,
      }}
    >
      {props.children}
    </DataContext.Provider>
  );
};

export { useData, DataProvider, useDataLoading };
